import React, { useState, Component } from 'react';
import { FileText, Users, Image as ImageIcon, MessageSquareQuote, LayoutTemplate, Type, Bold, Calendar } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Tooltip, Legend } from 'recharts';
import { Tabs, TabsContent } from '../components/ui/tabs';
import { articles, sentimentOverTime, coverageByTier, topSources, spokespersonMentions, contentTypeLabels } from '../lib/data';
const COLORS = ['#0187c3', '#1A3A5C', '#64B5F6', '#0D5F8A', '#90CAF9', '#003F6B'];
function KPI({
  label,
  value,
  icon: Icon
}: {
  label: string;
  value: string | number;
  icon: React.ElementType;
}) {
  return <div className="bg-card border border-border rounded-lg p-6 flex flex-col gap-3" data-id="element-1473">
      <div className="flex items-center gap-2 text-sm text-muted-foreground font-normal" data-id="element-1474">
        <Icon className="h-4 w-4" data-id="element-1475" />
        {label}
      </div>
      <div className="text-3xl md:text-4xl font-light leading-none text-foreground" style={{
      fontVariantNumeric: 'tabular-nums'
    }} data-id="element-1476">
        {value}
      </div>
    </div>;
}
function getSentimentColor(sentiment: string) {
  switch (sentiment) {
    case 'positive':
      return 'bg-[#0187c3]';
    case 'neutral':
      return 'bg-[#64B5F6]';
    case 'negative':
      return 'bg-[#1A3A5C]';
    default:
      return 'bg-muted';
  }
}
export function Statistics() {
  const [activeTab, setActiveTab] = useState('panoramica');
  const [startDate, setStartDate] = useState('2025-04-01');
  const [endDate, setEndDate] = useState('2025-04-15');
  // Calculate KPIs
  const frontPageCount = articles.filter(a => a.frontPageCallout).length;
  const uniqueAuthors = new Set(articles.map(a => a.author)).size;
  const withPhotoCount = articles.filter(a => a.hasPhoto).length;
  const titleMentionCount = articles.filter(a => a.titleMentionsClient).length;
  const boldReferenceCount = articles.filter(a => a.boldedReference).length;
  // Calculate Content Type Distribution
  const contentTypeCounts = articles.reduce((acc, article) => {
    acc[article.contentType] = (acc[article.contentType] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const contentTypeData = Object.entries(contentTypeCounts).map(([type, count]) => ({
    type,
    label: contentTypeLabels[type as keyof typeof contentTypeLabels],
    count,
    percentage: count / articles.length * 100
  })).sort((a, b) => b.count - a.count);
  // Calculate Top Authors
  const authorStats = articles.reduce((acc, article) => {
    if (!acc[article.author]) {
      acc[article.author] = {
        count: 0,
        totalScore: 0
      };
    }
    acc[article.author].count += 1;
    acc[article.author].totalScore += article.relevanceScore;
    return acc;
  }, {} as Record<string, {
    count: number;
    totalScore: number;
  }>);
  const topAuthorsList = Object.entries(authorStats).map(([name, stats]) => ({
    name,
    count: stats.count,
    avgScore: stats.totalScore / stats.count
  })).sort((a, b) => b.count - a.count).slice(0, 5);
  // Calculate Sections
  const sectionCounts = articles.reduce((acc, a) => {
    acc[a.section] = (acc[a.section] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const sectionData = Object.entries(sectionCounts).map(([name, count]) => ({
    name,
    count,
    percentage: count / articles.length * 100
  })).sort((a, b) => b.count - a.count);
  // Calculate Geo Distribution (Mock mapping)
  const getRegion = (source: string) => {
    const s = source.toLowerCase();
    if (s.includes('messaggero')) return 'Lazio';
    if (s.includes('mattino')) return 'Campania';
    if (s.includes('gazzettino')) return 'Veneto';
    if (s.includes('nazione')) return 'Toscana';
    if (s.includes('secolo')) return 'Liguria';
    return 'Nazionale';
  };
  const geoCounts = articles.reduce((acc, a) => {
    const reg = getRegion(a.source);
    acc[reg] = (acc[reg] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const geoData = Object.entries(geoCounts).map(([name, count]) => ({
    name,
    count,
    percentage: count / articles.length * 100
  })).sort((a, b) => b.count - a.count);
  const maxTopSourceArticles = Math.max(...topSources.map(s => s.articles));
  // Reusable Components for duplicated sections across tabs
  const SentimentChart = () => <div className="bg-card border border-border rounded-lg p-6" data-id="element-1477">
      <h3 className="text-lg font-medium mb-6" data-id="element-1478">Sentiment nel tempo</h3>
      <div className="h-64" data-id="element-1479">
        <ResponsiveContainer width="100%" height="100%" data-id="element-1480">
          <AreaChart data={sentimentOverTime} margin={{
          top: 5,
          right: 0,
          bottom: 0,
          left: -20
        }} data-id="element-1481">
            <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E5" vertical={false} data-id="element-1482" />
            <XAxis dataKey="date" tick={{
            fontSize: 12
          }} stroke="#8B8680" axisLine={false} tickLine={false} dy={10} data-id="element-1483" />
            <YAxis tick={{
            fontSize: 12
          }} stroke="#8B8680" axisLine={false} tickLine={false} data-id="element-1484" />
            <Tooltip data-id="element-1485" />
            <Area type="monotone" dataKey="positive" stackId="1" stroke="#0187c3" fill="#0187c3" fillOpacity={0.5} strokeWidth={1.5} data-id="element-1486" />
            <Area type="monotone" dataKey="neutral" stackId="1" stroke="#64B5F6" fill="#64B5F6" fillOpacity={0.35} strokeWidth={1.5} data-id="element-1487" />
            <Area type="monotone" dataKey="negative" stackId="1" stroke="#1A3A5C" fill="#1A3A5C" fillOpacity={0.5} strokeWidth={1.5} data-id="element-1488" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>;
  const SectionsChart = () => <div className="bg-card border border-border rounded-lg p-6" data-id="element-1489">
      <h3 className="text-lg font-medium mb-6" data-id="element-1490">Distribuzione per sezione</h3>
      <div className="h-72" data-id="element-1491">
        <ResponsiveContainer width="100%" height="100%" data-id="element-1492">
          <BarChart data={sectionData} margin={{
          top: 5,
          right: 0,
          left: -20,
          bottom: 5
        }} data-id="element-1493">
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E8E8E5" data-id="element-1494" />
            <XAxis dataKey="name" tick={{
            fontSize: 12
          }} stroke="#8B8680" axisLine={false} tickLine={false} dy={10} data-id="element-1495" />
            <YAxis tick={{
            fontSize: 12
          }} stroke="#8B8680" axisLine={false} tickLine={false} data-id="element-1496" />
            <Tooltip cursor={{
            fill: 'rgba(0,0,0,0.05)'
          }} data-id="element-1497" />
            <Bar dataKey="count" fill="#0187c3" radius={[4, 4, 0, 0]} barSize={40} data-id="element-1498" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>;
  const TopAuthorsList = () => <div className="bg-card border border-border rounded-lg p-6" data-id="element-1499">
      <h3 className="text-lg font-medium mb-6" data-id="element-1500">Top autori</h3>
      <div className="divide-y divide-border" data-id="element-1501">
        {topAuthorsList.map(author => <div key={author.name} className="flex items-center justify-between py-3 first:pt-0 last:pb-0" data-id="element-1502">
            <div className="text-[15px] font-normal text-foreground truncate pr-4" data-id="element-1503">
              {author.name}
            </div>
            <div className="flex items-center gap-6 flex-shrink-0 text-right" data-id="element-1504">
              <div data-id="element-1505">
                <div className="font-light text-base" style={{
              fontVariantNumeric: 'tabular-nums'
            }} data-id="element-1506">
                  {author.count}
                </div>
                <div className="text-[11px] text-muted-foreground uppercase tracking-wider font-normal" data-id="element-1507">
                  Articoli
                </div>
              </div>
              <div data-id="element-1508">
                <div className="font-light text-base text-accent" style={{
              fontVariantNumeric: 'tabular-nums'
            }} data-id="element-1509">
                  {author.avgScore.toFixed(1)}
                </div>
                <div className="text-[11px] text-muted-foreground uppercase tracking-wider font-normal" data-id="element-1510">
                  Score
                </div>
              </div>
            </div>
          </div>)}
      </div>
    </div>;
  return <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col gap-[0px] w-full" data-id="element-1511">
      {/* Header */}
      <div className="pt-6 md:pt-8 px-6 max-w-[1400px] mx-auto w-full bg-[#FFFFFF]" data-id="element-1512">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6" data-id="element-1513">
          <h1 className="text-2xl md:text-3xl font-normal tracking-tight" data-id="element-1514">
            Statistiche
          </h1>
          <div className="flex items-center gap-2 text-sm" data-id="element-1515">
            <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" data-id="element-1516" />
            <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2" data-id="element-1517">
              <label className="text-muted-foreground text-xs font-medium whitespace-nowrap" data-id="element-1518">
                Da
              </label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-transparent border-none outline-none text-sm text-foreground w-[130px] cursor-pointer" data-id="element-1519" />
              <span className="text-muted-foreground mx-1" data-id="element-1520">—</span>
              <label className="text-muted-foreground text-xs font-medium whitespace-nowrap" data-id="element-1521">
                A
              </label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-transparent border-none outline-none text-sm text-foreground w-[130px] cursor-pointer" data-id="element-1522" />
            </div>
          </div>
        </div>
      </div>

      {/* Tab bar - sticky */}
      <div className="border-b border-border sticky top-0 z-10 bg-background" data-id="element-1523">
        <div className="px-6 max-w-[1400px] mx-auto w-full flex items-end gap-0 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] bg-[#FFFFFF]" data-id="element-1524">
          {[{
          id: 'panoramica',
          label: 'Panoramica'
        }, {
          id: 'contenuto',
          label: 'Contenuto'
        }, {
          id: 'posizionamento',
          label: 'Posizionamento'
        }, {
          id: 'fonti',
          label: 'Fonti & Autori'
        }, {
          id: 'sentiment',
          label: 'Sentiment'
        }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className="relative px-5 py-3 text-sm transition-colors whitespace-nowrap" style={{
          backgroundColor: activeTab === tab.id ? '#FFFFFF' : 'transparent',
          color: activeTab === tab.id ? 'var(--foreground)' : 'var(--muted-foreground)',
          fontWeight: activeTab === tab.id ? 500 : 400
        }} data-id="element-1525">
              {tab.label}
              {activeTab === tab.id && <span className="absolute bottom-0 left-0 right-0" style={{
            height: '2px',
            backgroundColor: '#0187c3'
          }} data-id="element-1526" />}
            </button>)}
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-8 max-w-[1400px] mx-auto w-full" data-id="element-1527">
        <TabsContent value="panoramica" className="m-0 space-y-8" data-id="element-1528">
          {/* KPI Strip */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1529">
            <KPI label="Prime pagine" value={frontPageCount} icon={FileText} data-id="element-1530" />
            <KPI label="Autori unici" value={uniqueAuthors} icon={Users} data-id="element-1531" />
            <KPI label="Con foto" value={withPhotoCount} icon={ImageIcon} data-id="element-1532" />
            <KPI label="Titolo con menzione" value={titleMentionCount} icon={MessageSquareQuote} data-id="element-1533" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1534">
            <SentimentChart data-id="element-1535" />

            {/* Coverage by Tier (Radar) */}
            <div className="bg-card border border-border rounded-lg p-6" data-id="element-1536">
              <h3 className="text-lg font-medium mb-6" data-id="element-1537">Copertura per tier</h3>
              <div className="h-64" data-id="element-1538">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1539">
                  <RadarChart cx="50%" cy="50%" outerRadius="70%" data={coverageByTier} data-id="element-1540">
                    <PolarGrid stroke="#E8E8E5" data-id="element-1541" />
                    <PolarAngleAxis dataKey="tier" tick={{
                    fontSize: 12,
                    fill: '#8B8680'
                  }} data-id="element-1542" />
                    <PolarRadiusAxis angle={30} domain={[0, 'auto']} tick={false} axisLine={false} data-id="element-1543" />
                    <Radar name="Copertura" dataKey="percentage" stroke="#0187c3" fill="#0187c3" fillOpacity={0.5} data-id="element-1544" />
                    <Tooltip data-id="element-1545" />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="contenuto" className="m-0 space-y-8" data-id="element-1546">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1547">
            {/* Content Type Distribution (Donut) */}
            <div className="bg-card border border-border rounded-lg p-6" data-id="element-1548">
              <h3 className="text-lg font-medium mb-6" data-id="element-1549">
                Distribuzione per tipologia
              </h3>
              <div className="h-72" data-id="element-1550">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1551">
                  <PieChart data-id="element-1552">
                    <Pie data={contentTypeData} innerRadius={70} outerRadius={100} paddingAngle={2} dataKey="count" nameKey="label" data-id="element-1553">
                      {contentTypeData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-1554" />)}
                    </Pie>
                    <Tooltip data-id="element-1555" />
                    <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{
                    fontSize: '12px'
                  }} data-id="element-1556" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <SectionsChart data-id="element-1557" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1558">
            <TopAuthorsList data-id="element-1559" />
          </div>
        </TabsContent>

        <TabsContent value="posizionamento" className="m-0 space-y-8" data-id="element-1560">
          {/* Article Positioning */}
          <div className="bg-card border border-border rounded-lg p-6" data-id="element-1561">
            <h3 className="text-lg font-medium mb-6" data-id="element-1562">
              Posizionamento articoli
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-1563">
              <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30 border border-border" data-id="element-1564">
                <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center flex-shrink-0" data-id="element-1565">
                  <LayoutTemplate className="h-5 w-5 text-muted-foreground" data-id="element-1566" />
                </div>
                <div data-id="element-1567">
                  <div className="text-2xl font-light" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-1568">
                    {frontPageCount}
                  </div>
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wider mt-0.5 font-normal" data-id="element-1569">
                    Prima pagina
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30 border border-border" data-id="element-1570">
                <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center flex-shrink-0" data-id="element-1571">
                  <ImageIcon className="h-5 w-5 text-muted-foreground" data-id="element-1572" />
                </div>
                <div data-id="element-1573">
                  <div className="text-2xl font-light" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-1574">
                    {withPhotoCount}
                  </div>
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wider mt-0.5 font-normal" data-id="element-1575">
                    Con foto
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30 border border-border" data-id="element-1576">
                <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center flex-shrink-0" data-id="element-1577">
                  <Bold className="h-5 w-5 text-muted-foreground" data-id="element-1578" />
                </div>
                <div data-id="element-1579">
                  <div className="text-2xl font-light" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-1580">
                    {boldReferenceCount}
                  </div>
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wider mt-0.5 font-normal" data-id="element-1581">
                    Rif. in grassetto
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30 border border-border" data-id="element-1582">
                <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center flex-shrink-0" data-id="element-1583">
                  <Type className="h-5 w-5 text-muted-foreground" data-id="element-1584" />
                </div>
                <div data-id="element-1585">
                  <div className="text-2xl font-light" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-1586">
                    {titleMentionCount}
                  </div>
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wider mt-0.5 font-normal" data-id="element-1587">
                    Menzione titolo
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1588">
            <SectionsChart data-id="element-1589" />
          </div>
        </TabsContent>

        <TabsContent value="fonti" className="m-0 space-y-8" data-id="element-1590">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1591">
            {/* Top Sources (Horizontal Bar) */}
            <div className="bg-card border border-border rounded-lg p-6" data-id="element-1592">
              <h3 className="text-lg font-medium mb-6" data-id="element-1593">Top testate</h3>
              <div className="h-80" data-id="element-1594">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1595">
                  <BarChart layout="vertical" data={topSources} margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5
                }} data-id="element-1596">
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#E8E8E5" data-id="element-1597" />
                    <XAxis type="number" hide data-id="element-1598" />
                    <YAxis dataKey="name" type="category" width={100} tick={{
                    fontSize: 12,
                    fill: '#8B8680'
                  }} axisLine={false} tickLine={false} data-id="element-1599" />
                    <Tooltip cursor={{
                    fill: 'rgba(0,0,0,0.05)'
                  }} data-id="element-1600" />
                    <Bar dataKey="articles" fill="#0187c3" radius={[0, 4, 4, 0]} barSize={20} name="Articoli" data-id="element-1601" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <TopAuthorsList data-id="element-1602" />

            {/* Geo Distribution */}
            <div className="bg-card border border-border rounded-lg p-6 lg:col-span-2" data-id="element-1603">
              <h3 className="text-lg font-medium mb-6" data-id="element-1604">
                Distribuzione geografica
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-5" data-id="element-1605">
                {geoData.map(region => <div key={region.name} data-id="element-1606">
                    <div className="flex items-center justify-between mb-2 text-[15px]" data-id="element-1607">
                      <span className="text-foreground font-normal" data-id="element-1608">
                        {region.name}
                      </span>
                      <span className="text-muted-foreground font-normal" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-1609">
                        {region.count} art. ({region.percentage.toFixed(0)}%)
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden" data-id="element-1610">
                      <div className="h-full bg-accent rounded-full transition-all" style={{
                    width: `${region.percentage}%`
                  }} data-id="element-1611" />
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="sentiment" className="m-0 space-y-8" data-id="element-1612">
          <SentimentChart data-id="element-1613" />

          {/* Spokespersons */}
          <div className="bg-card border border-border rounded-lg p-6" data-id="element-1614">
            <h3 className="text-lg font-medium mb-6" data-id="element-1615">Portavoce citati</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1616">
              {spokespersonMentions.map(person => <div key={person.name} className="flex items-center justify-between p-4 border border-border rounded-md bg-muted/10" data-id="element-1617">
                  <div className="min-w-0 pr-4" data-id="element-1618">
                    <div className="text-[15px] font-normal text-foreground truncate" data-id="element-1619">
                      {person.name}
                    </div>
                    <div className="text-sm text-muted-foreground mt-0.5 font-normal" data-id="element-1620">
                      {person.role}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0" data-id="element-1621">
                    <span className="font-light text-lg" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-1622">
                      {person.mentions}
                    </span>
                    <div className={`w-2.5 h-2.5 rounded-full ${getSentimentColor(person.avgSentiment)}`} data-id="element-1623" />
                  </div>
                </div>)}
            </div>
          </div>
        </TabsContent>
      </div>
    </Tabs>;
}